import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Waves, Map as MapIcon } from "lucide-react";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Solutions", to: "/solutions" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "About", to: "/about" },
];

const testId = (label) => label.toLowerCase().replace(/\s/g, "-");

const useScrolled = (threshold = 40) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};

const Brand = ({ dark }) => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
    <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-md shadow-cyan-500/30">
      <Waves className="w-5 h-5 text-slate-950 group-hover:scale-110 transition-transform" />
    </div>
    <div className="leading-none">
      <span className={`block font-bold text-lg tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>Lake Monitor</span>
      <span className={`block font-mono text-[10px] tracking-[0.3em] uppercase ${dark ? "text-cyan-300" : "text-cyan-700"}`}>AI Mapping</span>
    </div>
  </Link>
);

const DesktopNav = ({ dark }) => {
  const linkClass = ({ isActive }) =>
    `px-4 py-2 rounded-lg text-[15px] font-medium transition-all ${
      dark
        ? isActive ? "text-cyan-300 bg-white/10" : "text-slate-200 hover:text-white hover:bg-white/10"
        : isActive ? "text-cyan-700 bg-cyan-50" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
    }`;
  return (
    <nav aria-label="Main navigation" className="hidden lg:flex items-center gap-1">
      {NAV.map((item) => (
        <NavLink key={item.to} to={item.to} data-testid={`nav-${testId(item.label)}`} className={linkClass} end={item.to === "/"}>
          {item.label}
        </NavLink>
      ))}
      <Link
        to="/#sample-map"
        data-testid="nav-sample-map"
        className={`ml-1 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[15px] font-semibold border transition-all ${
          dark ? "text-cyan-200 border-cyan-400/40 bg-cyan-400/10 hover:bg-cyan-400/20" : "text-cyan-800 border-cyan-300 bg-cyan-50 hover:bg-cyan-100"
        }`}
      >
        <MapIcon className="w-4 h-4" /> Sample Map
      </Link>
    </nav>
  );
};

const MobileMenu = ({ onClose }) => (
  <div className="lg:hidden glass-light border-t border-slate-200 px-4 py-4 space-y-1 shadow-lg">
    {NAV.map((item) => (
      <NavLink
        key={item.to}
        to={item.to}
        data-testid={`mobile-nav-${testId(item.label)}`}
        className={({ isActive }) => `block px-4 py-3 rounded-lg text-base font-medium ${isActive ? "text-cyan-700 bg-cyan-50" : "text-slate-800"}`}
        end={item.to === "/"}
      >
        {item.label}
      </NavLink>
    ))}
    <Link
      to="/#sample-map"
      data-testid="mobile-nav-sample-map"
      onClick={onClose}
      className="flex items-center gap-2 px-4 py-3 rounded-lg text-base font-semibold text-cyan-800 bg-cyan-50 border border-cyan-200"
    >
      <MapIcon className="w-4 h-4" /> Sample Map
    </Link>
    <Link
      to="/request-a-quote"
      data-testid="mobile-nav-request-quote-button"
      className="block text-center bg-gradient-to-r from-cyan-600 to-teal-600 text-white font-bold px-5 py-3 rounded-xl mt-2"
    >
      Request a Quote
    </Link>
  </div>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const scrolled = useScrolled();
  const dark = pathname === "/" && !scrolled;

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${dark ? "bg-transparent" : "glass-light border-b border-slate-200 shadow-sm"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Brand dark={dark} />
          <DesktopNav dark={dark} />
          <div className="hidden lg:block">
            <Link
              to="/request-a-quote"
              data-testid="nav-request-quote-button"
              className={`inline-flex items-center font-bold px-5 py-2.5 rounded-xl transition-all hover:-translate-y-0.5 ${
                dark
                  ? "bg-gradient-to-r from-cyan-400 to-teal-400 text-slate-950 shadow-[0_0_20px_rgba(0,240,255,0.3)]"
                  : "bg-gradient-to-r from-cyan-600 to-teal-600 text-white shadow-lg shadow-cyan-600/25"
              }`}
            >
              Request a Quote
            </Link>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className={`lg:hidden p-2 ${dark ? "text-white" : "text-slate-900"}`}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && <MobileMenu onClose={() => setOpen(false)} />}
    </header>
  );
};
